// src/autodesk_usage/components/customerDetail/ProductTrendCard.tsx
// All comments in English only
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Bar,
} from "recharts";

interface Point {
  usage_month: string;
  used_users: number;
  active_seats: number;
}

interface Props {
  product: string;
  series: Point[];
}

export default function ProductTrendCard({ product, series }: Props) {
  const rows = (series || []).map((p) => {
    // Ensure numeric values
    const used = Number(p.used_users ?? 0);
    const seats = Number(p.active_seats ?? 0);
    // Split the gap into two positive-only bars starting from 0
    const overuse_gap = used > seats ? used - seats : 0;     // overuse side
    const unused_gap = seats > used ? seats - used : 0;      // unused capacity side
    return {
      month: (p.usage_month || "").slice(0, 7),
      users: used,
      seats: seats,
      overuse_gap,
      unused_gap,
    };
  });

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{product} — Used Users vs Active Seats (Monthly)</CardTitle>
      </CardHeader>
      <CardContent className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ left: 8, right: 8, top: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" minTickGap={24} />
            {/* Force Y axis from 0 so bars start at zero */}
            <YAxis allowDecimals={false} domain={[0, "auto"]} />
            <Tooltip />
            <Legend />

            {/* Bars: both start at 0; use the same stackId so they line up per month */}
            <Bar
              dataKey="overuse_gap"
              name="Overuse Gap"
              stackId="gap"
              barSize={20}
              fill="#f59e0b"       // amber-500
              opacity={0.75}
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey="unused_gap"
              name="Unused Capacity"
              stackId="gap"
              barSize={20}
              fill="#94a3b8"       // slate-400
              opacity={0.7}
              radius={[4, 4, 0, 0]}
            />

            {/* Lines */}
            <Line
              type="monotone"
              dataKey="users"
              name="Used Users"
              dot={false}
              stroke="#ef4444"     // red-500
              strokeWidth={2.5}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="seats"
              name="Active Seats"
              dot={false}
              stroke="#3b82f6"     // blue-500
              strokeWidth={2.5}
              activeDot={{ r: 5 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
